import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlay, faCirclePause } from '@fortawesome/free-solid-svg-icons';
import ResponsiveTable from './ResponsiveTable';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function SongsTable({ data, totalCount, currentPage, pageSize, me, searchKeyword }) {
  const navigate = useNavigate();
  const { playImmediate, playlist, currentIndex, isPlaying, togglePlay } = useMusicPlayer();
  const currentTrack = currentIndex !== null ? playlist[currentIndex] : null;

  const handlePlay = useCallback(
    (row) => {
      const song = row.original;
      if (!song?.fileUrl) return;
      if (currentTrack && currentTrack.id === song.id) {
        togglePlay();
        return;
      }
      playImmediate([{
        id: song.id,
        nameKr: song.nameKr,
        artistKr: song.album?.artist?.nameKr || '',
        fileUrl: song.fileUrl,
        albumImage: song.album?.image || FALLBACK_IMG,
        albumName: song.album?.nameKr || '',
      }]);
    },
    [currentTrack, playImmediate, togglePlay]
  );

  const isTrackPlaying = useCallback(
    (songId) => currentTrack && currentTrack.id === songId && isPlaying,
    [currentTrack, isPlaying]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: '순번',
        id: 'row',
        Cell: ({ row }) =>
          totalCount - ((currentPage - 1) * pageSize + row.index),
      },
      {
        Header: '듣기',
        accessor: 'fileUrl',
        Cell: ({ value, row }) =>
          value ? (
            <span
              className="inline-flex align-middle items-center justify-center cursor-pointer text-2xl"
              onClick={() => handlePlay(row)}
              title={isTrackPlaying(row.original.id) ? '일시정지' : '재생'}>
              <FontAwesomeIcon
                icon={isTrackPlaying(row.original.id) ? faCirclePause : faCirclePlay}
                className={
                  isTrackPlaying(row.original.id)
                    ? 'text-red-400 hover:text-red-700'
                    : 'text-sky-500 hover:text-sky-700'
                }
              />
            </span>
          ) : (
            <span className="text-slate-500">-</span>
          ),
      },
      {
        Header: () => (
          <div className="whitespace-nowrap min-w-[64px] text-center">
            앨범 커버
          </div>
        ),
        accessor: 'album.image',
        Cell: ({ row }) => (
          <img
            src={row.original.album?.image || FALLBACK_IMG}
            alt={row.original.album?.nameKr}
            className="w-12 h-12 rounded-full object-cover mx-auto border border-white/[0.08]"
          />
        ),
      },
      {
        Header: '곡명',
        accessor: 'nameKr',
        Cell: ({ row }) => (
          <span
            className="font-bold text-sky-400 cursor-pointer hover:underline"
            onClick={() => {
              const searchParam = searchKeyword ? `&search=${encodeURIComponent(searchKeyword)}` : '';
              navigate(`/Songs/${row.original.id}?page=${currentPage}${searchParam}`);
            }}>
            {row.original.nameKr}
          </span>
        ),
      },
      {
        Header: '앨범명',
        accessor: 'album.nameKr',
      },
      {
        Header: '아티스트',
        accessor: 'album.artist.nameKr',
      },
      {
        Header: '권리사',
        accessor: 'clientDisplay',
      },
      {
        Header: '발매일',
        accessor: 'album.releaseDateTime',
      },
    ],
    [totalCount, currentPage, pageSize, navigate, handlePlay, isTrackPlaying, searchKeyword]
  );

  return (
    <ResponsiveTable
      columns={columns}
      data={data}
      visibleOnMobile={['row', 'album.image', 'nameKr', 'album.artist.nameKr']}
    />
  );
}

export default SongsTable;
